import { isNotificationNavigationBlocked } from "./notifications";
import { rewriteReportDomain } from "../../configs/environment";
import { getParams } from "../../utlis/helpers";
import { MOBILITY_REPORT } from "../../routes/routerPath";
import { getCurrentLocale } from "../../utlis/locale";
import {
  getActiveLocale,
  getNameOrder,
  resolveLocale,
  SESSION_STORAGE_KEY,
} from "../../configs/regionalProfile";
import { clearActiveRegion } from "../../configs/activeRegion";
import { MOBILITY_REPORT_ENGINE } from "../../utlis/mobilityReportEngine";

export const CLIENT_INOCHI = "inochi";
// Defined in configs/regionalProfile so the locale resolver can read the
// stored session without importing this module. Re-exported unchanged.
export { SESSION_STORAGE_KEY };

// The one effective locale for the app. Kept as the historical export name;
// it now returns the clamped locale rather than the raw one.
export const getLocale = () => getActiveLocale();

export const encodeBase64 = (value) => {
  try {
    return window.btoa(unescape(encodeURIComponent(value)));
  } catch {
    return window.btoa(value);
  }
};

export const getSessionFromUrl = () => {
  const params = getParams();
  if (!params.accessToken || !params.staffId) return null;
  return {
    accessToken: params.accessToken,
    staffId: params.staffId,
    providerId: params.providerId || "",
    adminId: params.adminId || "",
    clientId: params.clientId || "",
    // The shell-injected `locale` parameter is untrusted input and must pass
    // the same allowed-locale check as any other source, or it becomes a way
    // to run a market in a language its build does not ship.
    locale: resolveLocale(params.locale || getCurrentLocale()),
    client: params.client || CLIENT_INOCHI,
  };
};

/**
 * A session persisted by an older build, or by a build for a different market,
 * can carry a locale this market does not enable. The locale is clamped BEFORE
 * anything is written back, so a stale value is never re-persisted and cannot
 * be read as trusted on the next load. Every other field is preserved exactly.
 */
const normalizeStoredSession = (stored, raw) => {
  const normalized = { ...stored, locale: resolveLocale(stored.locale) };
  const value = JSON.stringify(normalized);
  // Write when the stored copy differs from the normalized one, or when
  // sessionStorage still needs re-hydrating after an app/browser restart.
  if (value !== raw || !sessionStorage.getItem(SESSION_STORAGE_KEY)) {
    setStoredSession(normalized);
  }
  return normalized;
};

export const getStoredSession = () => {
  const fromUrl = getSessionFromUrl();
  if (fromUrl) {
    setStoredSession(fromUrl);
    return fromUrl;
  }
  try {
    const raw =
      sessionStorage.getItem(SESSION_STORAGE_KEY) ||
      localStorage.getItem(SESSION_STORAGE_KEY);
    const stored = JSON.parse(raw || "null");
    if (!stored?.accessToken) return null;
    // Re-hydrate sessionStorage after an app/browser restart so code that
    // reads the session directly from sessionStorage keeps working.
    return normalizeStoredSession(stored, raw);
  } catch {
    return null;
  }
};

export const setStoredSession = (session) => {
  const value = JSON.stringify(session);
  // localStorage keeps the user signed in across app/browser restarts;
  // sessionStorage is kept in sync for legacy direct readers.
  localStorage.setItem(SESSION_STORAGE_KEY, value);
  sessionStorage.setItem(SESSION_STORAGE_KEY, value);
};

export const clearStoredSession = () => {
  clearActiveRegion();
  localStorage.removeItem(SESSION_STORAGE_KEY);
  localStorage.removeItem("lastVisitedUrl");
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem("userList");
  sessionStorage.removeItem("userIndex");
  sessionStorage.removeItem("testMModule");
  sessionStorage.removeItem("mmtPendingNativePushes");
};

export const sessionQuery = (session, extra = {}) => {
  const query = new URLSearchParams({
    staffId: session.staffId,
    accessToken: session.accessToken,
    locale: getActiveLocale(),
    providerId: session.providerId || "",
    adminId: session.adminId || "",
    clientId: session.clientId || "",
    client: session.client || CLIENT_INOCHI,
    tab: "metrics",
    hideNav: "true",
    ...extra,
  });
  return query.toString();
};

export const buildSessionPath = (path, session, extra = {}) =>
  `${path}?${sessionQuery(session, extra)}`;

export const buildDashboardUrl = (session, seniorId, quickJumpHash) => {
  const query = new URLSearchParams(sessionQuery(session, { seniorId }));
  query.delete("hideNav");
  const base = `/person-dashboard?${query.toString()}`;
  return quickJumpHash ? `${base}#${quickJumpHash}` : base;
};

/**
 * Patient page opened from the list: the Dynamic Action Plan view (DAP87),
 * i.e. the Action tab. Other entry points keep their own tab.
 */
export const buildPatientPageUrl = (session, seniorId) => {
  const [path, hash] = buildDashboardUrl(session, seniorId).split("#");
  const [base, search = ""] = path.split("?");
  const query = new URLSearchParams(search);
  query.set("tab", "action");
  return `${base}?${query.toString()}${hash ? `#${hash}` : ""}`;
};

export const buildActivityMonitoringUrl = (session, seniorId) =>
  buildSessionPath("/activity-monitoring", session, {
    seniorId,
    tab: "monitor",
  });

// Reads a query param out of a (possibly hash-suffixed) report URL.
const paramFromUrl = (rawUrl, key) => {
  if (!rawUrl) return null;
  const query = String(rawUrl).split("#")[0].split("?")[1] || "";
  return new URLSearchParams(query).get(key);
};

// Opens the native, in-app mobility report (/mobility-report-view) instead of
// the external web report. The moduleId/reportId come from the notification's
// quickJump when present, otherwise they are parsed out of the report URL.
export const buildMobilityReportUrl = (session, quickJump = {}, openUrl) => {
  const reportUrl = openUrl || quickJump.reportURL;
  const moduleId = quickJump.moduleId ?? paramFromUrl(reportUrl, "moduleId");
  const reportId =
    quickJump.reportId ??
    quickJump.report_id ??
    paramFromUrl(reportUrl, "reportId") ??
    paramFromUrl(reportUrl, "report_id");
  const revision = quickJump.revision ?? paramFromUrl(reportUrl, "revision");
  const activityId =
    quickJump.activity_id ??
    quickJump.activityId ??
    paramFromUrl(reportUrl, "activity_id") ??
    paramFromUrl(reportUrl, "activityId");

  const query = new URLSearchParams(
    sessionQuery(session, { seniorId: quickJump.seniorId || "" }),
  );
  query.delete("hideNav");
  if (quickJump.seniorId) query.set("seniorUuid", quickJump.seniorId);
  if (moduleId != null && moduleId !== "") {
    query.set("moduleId", String(moduleId));
  }
  if (reportId != null && reportId !== "") {
    query.set("reportId", String(reportId));
  }
  if (revision != null && revision !== "")
    query.set("revision", String(revision));
  if (activityId != null && activityId !== "")
    query.set("activity_id", String(activityId));
  // The v2 render document is always addressed through the /v6 report API; the
  // v95 engine opens the notified test's own report (moduleId / reportId).
  if (MOBILITY_REPORT_ENGINE === "v2") query.set("reportSource", "v6");
  return `${MOBILITY_REPORT}?${query.toString()}`;
};

export const openAddTestPerson = (session, openModal) => {
  openModal({ type: "add" });
};

/* Import uses the same modal channel as Add and Edit. */
export const openImportPeople = (session, openModal) => {
  openModal({ type: "import" });
};

export const openEditTestPerson = (session, seniorUuid, openModal) => {
  openModal({ type: "edit", seniorUuid });
};

export const openViewTestPerson = (session, seniorUuid, openModal) => {
  openModal({ type: "view", seniorUuid });
};

export const quickJumpToHash = (destination) =>
  String(destination || "").replace(/_/g, "-");

export const appendQueryParamsToUrl = (rawUrl, params) => {
  const [withoutHash, hash = ""] = String(rawUrl).split("#");
  const [base, query = ""] = withoutHash.split("?");
  const search = new URLSearchParams(query);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  const hashSuffix = hash ? `#${hash}` : "";
  return `${base}?${search.toString()}${hashSuffix}`;
};

export const appendTabToUrl = (url, tab) =>
  appendQueryParamsToUrl(url, { tab });

export const normalizeReportUrl = (rawUrl, session) => {
  const replaced = String(rawUrl || "").replace(/reportv1/i, "reportv2");
  return appendQueryParamsToUrl(rewriteReportDomain(replaced), {
    isWebsite: "true",
    accessToken: session.accessToken,
    staffId: session.staffId,
    providerId: session.providerId,
    adminId: session.adminId,
    clientId: session.clientId,
    tab: "metrics",
    hideNav: "true",
    reportId:
      new URLSearchParams((replaced.split("?")[1] || "").split("#")[0]).get(
        "reportId",
      ) || "latest",
    appType:
      new URLSearchParams((replaced.split("?")[1] || "").split("#")[0]).get(
        "appType",
      ) || "sl",
    metrics:
      new URLSearchParams((replaced.split("?")[1] || "").split("#")[0]).get(
        "metrics",
      ) || "true",
  });
};

export const buildNotificationDestination = (notification, session) => {
  if (
    !notification ||
    !session ||
    isNotificationNavigationBlocked(notification)
  )
    return "";

  const source = String(
    notification.source || notification.sourceTool || "",
  ).toUpperCase();
  const quickJump = notification.quickJump || {};

  if (notification.category === "SENSOR_DATA" && source === "WELLTRA") {
    return appendTabToUrl(
      buildDashboardUrl(session, quickJump.seniorId || ""),
      "monitor",
    );
  }

  if (source === "PONTOSENSE") {
    return buildActivityMonitoringUrl(session, quickJump.seniorId || "");
  }

  // "See Report" / "See Report Info" / "See Risk Metrics" open the native
  // in-app mobility report rather than launching the external web report.
  const action = String(quickJump.action || "").toUpperCase();
  if (action.startsWith("SEE_REPORT") || action === "SEE_RISK_METRICS") {
    return buildMobilityReportUrl(session, quickJump, notification.openUrl);
  }

  if (action === "SEE_RISK_INSIGHTS" && quickJump.reportId) {
    return appendQueryParamsToUrl(
      appendTabToUrl(
        buildDashboardUrl(session, quickJump.seniorId || ""),
        "risk",
      ),
      { reportId: quickJump.reportId },
    );
  }

  if (notification.openUrl) {
    return appendQueryParamsToUrl(
      normalizeReportUrl(notification.openUrl, session),
      { moduleId: quickJump.moduleId },
    );
  }

  const hash = quickJump.destination
    ? quickJumpToHash(quickJump.destination)
    : undefined;
  return appendQueryParamsToUrl(
    buildDashboardUrl(session, quickJump.seniorId || "", hash),
    { moduleId: quickJump.moduleId },
  );
};

export const openNotificationDestination = (
  notification,
  session,
  navigate,
) => {
  const url = buildNotificationDestination(notification, session);
  if (!url) return false;

  if (url.startsWith("/") && navigate) navigate(url);
  else window.location.href = url;
  return true;
};

/**
 * Display order for a person's name.
 *
 * v92 tested `locale === "en"`, so every non-English locale fell into the
 * Last-First branch. That was harmless while English and Japanese were the only
 * shipping languages, but it means a US build running Spanish, or a Canadian
 * build running French, would silently reverse every name on the list. Order is
 * a regional convention, not a language one, so it now comes from the
 * configured regional profile.
 *
 * Callers may still pass a third locale argument; it is ignored and order comes
 * from the market profile.
 */
export const formatPersonName = (firstName, lastName) => {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  const [a, b] =
    getNameOrder() === "last_first" ? [last, first] : [first, last];
  // Initials-only people (first "J", last "T") read as "JT", not "J T".
  if (a.length === 1 && b.length === 1) return `${a}${b}`.toUpperCase();
  return `${a} ${b}`.trim();
};
